import { isObject } from "lodash";

import type { ChatRoom } from "@/react/types/Chat/ChatRoom";

type UpdateQueryCache = (updatedChatRooms: any[]) => void;

export const onChatMarkAllAsRead = (
  eventData: any,
  chatRooms: any[],
  updateQueryCache: (updatedChatRooms: ChatRoom[]) => void,
) => {
  const { chat_room_uuids: chatRoomUuids = [] } = eventData;
  if (chatRoomUuids.length === 0) return;
  const updatedChatRooms = chatRooms.map((chatRoom: ChatRoom) => {
    if (chatRoomUuids.includes(chatRoom.uuid)) {
      return { ...chatRoom, has_unread_messages: false };
    }
    return chatRoom;
  });
  updateQueryCache(updatedChatRooms);
};

export const onChatRoomRead = (
  eventData: any,
  chatRooms: any[],
  updateQueryCache: (updatedChatRooms: ChatRoom[]) => void,
) => {
  const { chat_room_uuid } = eventData;
  const chatRoomIndex = chatRooms.findIndex(
    chatRoom => chatRoom.uuid === chat_room_uuid,
  );
  if (chatRoomIndex > -1) {
    const messageChatRoom = chatRooms[chatRoomIndex];
    const updatedChatRooms = [
      ...chatRooms.slice(0, chatRoomIndex),
      { ...messageChatRoom, has_unread_messages: false },
      ...chatRooms.slice(Number(chatRoomIndex) + 1),
    ];
    updateQueryCache(updatedChatRooms);
  }
};

export const onChatRoomUpdated = (
  eventData: any,
  chatRooms: any[],
  updateQueryCache: UpdateQueryCache,
) => {
  const chat_room =
    typeof eventData.chat_room === "string"
      ? JSON.parse(eventData.chat_room)
      : eventData.chat_room;
  const updatedChatRooms = chatRooms.map(chatRoom => {
    if (chatRoom.uuid === chat_room.uuid) {
      return { ...chatRoom, chat_room_name: chat_room.chat_room_name };
    }
    return chatRoom;
  });
  updateQueryCache(updatedChatRooms);
};

export const onChatRoomParticipantsUpdated = (
  eventData: any,
  chatRooms: any[],
  updateQueryCache: UpdateQueryCache,
) => {
  const updatedChatRoom = isObject(eventData.chat_room)
    ? eventData.chat_room
    : JSON.parse(eventData.chat_room);
  const updatedChatRooms = chatRooms.map((chatRoom: any) => {
    if (chatRoom.uuid === updatedChatRoom.uuid) {
      return { ...chatRoom, ...updatedChatRoom };
    }
    return chatRoom;
  });
  updateQueryCache(updatedChatRooms);
};

export const onChatRoomPinned = (
  eventData: any,
  chatRooms: any[],
  updateQueryCache: UpdateQueryCache,
) => {
  const { chat_room_id: pinnedChatRoomId, pinned_at: pinnedAt } =
    eventData || {};
  let pinnedChatRoom: any = {};
  const otherChatRooms: any[] = [];
  chatRooms.forEach(chatRoom => {
    if (chatRoom.id !== pinnedChatRoomId) {
      otherChatRooms.push(chatRoom);
    } else {
      pinnedChatRoom = { ...chatRoom, pinned_at: pinnedAt };
    }
  });
  updateQueryCache([pinnedChatRoom, ...otherChatRooms]);
};

export const onChatRoomUnpinned = (
  eventData: any,
  chatRooms: any[],
  updateQueryCache: UpdateQueryCache,
  fetchUserChatRooms: () => unknown,
) => {
  const { chat_room_id: unPinnedChatRoomId } = eventData || {};
  const updatedChatRooms: any[] = chatRooms.map(chatRoom => {
    if (chatRoom.id === unPinnedChatRoomId) {
      return { ...chatRoom, pinned_at: null };
    }
    return chatRoom;
  });
  updateQueryCache([...updatedChatRooms]);
  void fetchUserChatRooms(); // Reload chat rooms to get back to the original position
};
